import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { config } from '../../utils/config';
import DatePicker from '../../components/DatePicker/DatePicker';

const CreateFundraising = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [teams, setTeams] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    goal: '',
    startDate: '',
    endDate: '',
    selectedTeams: [],
    donationOptions: [
      { amount: '', label: '' }
    ],
    image: null,
    allowCustomDonation: true
  });

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/teams`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch teams');

      const data = await response.json();
      setTeams(data.results || []);
    } catch (error) {
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addDonationOption = () => {
    setFormData(prev => ({
      ...prev,
      donationOptions: [...prev.donationOptions, { amount: '', label: '' }]
    }));
  };

  const updateDonationOption = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      donationOptions: prev.donationOptions.map((opt, i) =>
        i === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const removeDonationOption = (index) => {
    setFormData(prev => ({
      ...prev,
      donationOptions: prev.donationOptions.filter((_, i) => i !== index)
    }));
  };

  const toggleTeamSelection = (teamId) => {
    setFormData(prev => ({
      ...prev,
      selectedTeams: prev.selectedTeams.includes(teamId)
        ? prev.selectedTeams.filter(id => id !== teamId)
        : [...prev.selectedTeams, teamId]
    }));
  };

  const canProceedStep1 = () => {
    return formData.title.trim() && formData.subtitle.trim();
  };

  const canProceedStep2 = () => {
    return formData.goal && parseFloat(formData.goal) > 0;
  };

  const canProceedStep3 = () => {
    return formData.startDate && formData.endDate && formData.selectedTeams.length > 0;
  };

  const handleCreate = async () => {
    try {
      const campaignData = new FormData();
      campaignData.append('title', formData.title);
      campaignData.append('subtitle', formData.subtitle);
      campaignData.append('description', formData.description);
      campaignData.append('goal', parseFloat(formData.goal));
      campaignData.append('startDate', formData.startDate);
      campaignData.append('endDate', formData.endDate);
      campaignData.append('teamIds', JSON.stringify(formData.selectedTeams));
      campaignData.append('donationOptions', JSON.stringify(formData.donationOptions.filter(opt => opt.amount)));
      campaignData.append('allowCustomDonation', formData.allowCustomDonation);

      if (formData.image) {
        campaignData.append('image', formData.image);
      }

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/fundraising`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: campaignData
      });

      if (!response.ok) throw new Error('Failed to create campaign');

      const data = await response.json();
      navigate(`/fundraising/${data.id}`);
    } catch (error) {
      alert('Failed to create campaign. Please try again.');
    }
  };

  const StepIndicator = ({ step, label, active, completed }) => (
    <div className="flex items-center gap-2">
      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${active ? 'bg-[#00E7FF] text-[#000F1F]' : completed ? 'bg-[#082441] text-[#00E7FF]' : 'bg-[#192634] text-[#7F8585]'
        }`}>
        {completed ? '✓' : step}
      </div>
      <span className={`text-sm ${active ? 'text-[#00E7FF]' : completed ? 'text-[#FEFCEB]' : 'text-[#7F8585]'
        }`}>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#000F1F] text-[#FEFCEB]">
      <div className="max-w-[900px] mx-auto py-6 px-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : navigate('/fundraising')}
            className="text-[#7F8585] hover:text-[#FEFCEB] transition-colors"
          >
            ← Back to Fundraising
          </button>
          <h1 className="text-3xl font-bold text-[#FEFCEB] m-0">Create fundraising campaign</h1>
        </div>

        <div className="flex items-center justify-center gap-8 mb-8">
          <StepIndicator step={1} label="Enter general details" active={currentStep === 1} completed={currentStep > 1} />
          <div className="h-px w-16 bg-[#192634]" />
          <StepIndicator step={2} label="Enter goal and donation options" active={currentStep === 2} completed={currentStep > 2} />
          <div className="h-px w-16 bg-[#192634]" />
          <StepIndicator step={3} label="Select goal and duration options" active={currentStep === 3} completed={currentStep > 3} />
          <div className="h-px w-16 bg-[#192634]" />
          <StepIndicator step={4} label="Enter campaign details" active={currentStep === 4} completed={false} />
        </div>

        <div className="bg-[#011224] rounded-2xl border border-[#192634] p-8">
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-[#FEFCEB] text-sm mb-3">Campaign title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="New Kits for U/8 Soccer Team"
                  className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF]"
                />
              </div>

              <div>
                <label className="block text-[#FEFCEB] text-sm mb-3">Campaign subtitle</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="Help us raise funds for new team kits"
                  className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF]"
                />
              </div>

              <div>
                <label className="block text-[#FEFCEB] text-sm mb-3">Campaign details</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Tell supporters why you are fundraising and what the funds will be used for..."
                  rows={5}
                  className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] resize-none"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  onClick={() => navigate('/fundraising')}
                  className="px-6 py-2.5 text-[#7F8585] hover:text-[#FEFCEB] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!canProceedStep1()}
                  className="bg-[#00E7FF] hover:bg-[#00d4e6] text-[#000F1F] px-8 py-2.5 rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-[#FEFCEB] text-sm mb-3">Campaign goal (£)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.goal}
                  onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                  placeholder="2000"
                  className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF]"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-[#FEFCEB] text-sm">Donation options</label>
                  <button
                    onClick={addDonationOption}
                    className="flex items-center gap-2 text-[#00E7FF] hover:text-[#00d4e6] transition-colors text-sm"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 3v10m-5-5h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Add donation option
                  </button>
                </div>

                <div className="space-y-3">
                  {formData.donationOptions.map((option, index) => (
                    <div key={index} className="flex items-center gap-3 p-4 bg-[#082441] rounded-lg border border-[#192634]">
                      <span className="text-[#7F8585] font-medium">{index + 1}</span>
                      <input
                        type="text"
                        value={option.label}
                        onChange={(e) => updateDonationOption(index, 'label', e.target.value)}
                        placeholder="Team Sponsor"
                        className="flex-1 bg-[#011224] text-[#FEFCEB] rounded-lg px-4 py-2.5 border border-[#192634] outline-none focus:border-[#00E7FF]"
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-[#FEFCEB]">£</span>
                        <input
                          type="number"
                          step="0.01"
                          value={option.amount}
                          onChange={(e) => updateDonationOption(index, 'amount', e.target.value)}
                          placeholder="100"
                          className="w-24 bg-[#011224] text-[#FEFCEB] rounded-lg px-4 py-2.5 border border-[#192634] outline-none focus:border-[#00E7FF]"
                        />
                      </div>
                      <button
                        onClick={() => removeDonationOption(index)}
                        className="text-[#7F8585] hover:text-red-400 transition-colors text-xl"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="customDonation"
                  checked={formData.allowCustomDonation}
                  onChange={(e) => setFormData({ ...formData, allowCustomDonation: e.target.checked })}
                  className="w-5 h-5 rounded border-2 border-[#192634] bg-transparent checked:bg-[#00E7FF] checked:border-[#00E7FF]"
                />
                <label htmlFor="customDonation" className="text-[#FEFCEB] text-sm">
                  Allow custom donation amount
                </label>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-2.5 text-[#7F8585] hover:text-[#FEFCEB] transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  disabled={!canProceedStep2()}
                  className="bg-[#00E7FF] hover:bg-[#00d4e6] text-[#000F1F] px-8 py-2.5 rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-[#FEFCEB] text-sm mb-3">Start date</label>
                  <DatePicker
                    value={formData.startDate}
                    onChange={(date) => setFormData({ ...formData, startDate: date })}
                    disablePastDates={false}
                  />
                </div>

                <div>
                  <label className="block text-[#FEFCEB] text-sm mb-3">End date</label>
                  <DatePicker
                    value={formData.endDate}
                    onChange={(date) => setFormData({ ...formData, endDate: date })}
                    minDate={formData.startDate}
                    disablePastDates={false}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#FEFCEB] text-sm mb-3">Select teams</label>
                <div className="text-[#7F8585] text-sm mb-2">{formData.selectedTeams.length} teams selected</div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto p-2">
                  {teams.map(team => (
                    <div
                      key={team.id}
                      onClick={() => toggleTeamSelection(team.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${formData.selectedTeams.includes(team.id)
                          ? 'border-[#00E7FF] bg-[#082441]'
                          : 'border-[#192634] bg-[#011224] hover:border-[#7F8585]'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedTeams.includes(team.id)}
                        onChange={() => { }}
                        className="w-5 h-5 rounded border-2 border-[#192634] bg-transparent checked:bg-[#00E7FF] checked:border-[#00E7FF]"
                      />
                      {team.crest && (
                        <div
                          className="w-8 h-8 rounded-full bg-cover bg-center"
                          style={{ backgroundImage: `url(${team.crest})` }}
                        />
                      )}
                      <span className="text-[#FEFCEB] flex-1">{team.title}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2.5 text-[#7F8585] hover:text-[#FEFCEB] transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(4)}
                  disabled={!canProceedStep3()}
                  className="bg-[#00E7FF] hover:bg-[#00d4e6] text-[#000F1F] px-8 py-2.5 rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <label className="block text-[#FEFCEB] text-sm mb-3">Campaign image</label>
                <div className="border-2 border-dashed border-[#192634] rounded-lg p-8 text-center">
                  {imagePreview ? (
                    <div className="relative">
                      <img src={imagePreview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                      <button
                        onClick={() => {
                          setImagePreview(null);
                          setFormData({ ...formData, image: null });
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-[#FEFCEB] rounded-full w-8 h-8 flex items-center justify-center"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <div className="flex flex-col items-center">
                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="mb-4">
                          <path d="M24 12v24m-12-12h24" stroke="#7F8585" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                        <span className="text-[#00E7FF]">Upload image</span>
                        <span className="text-[#7F8585] text-sm mt-2">PNG, JPG up to 5MB</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="bg-[#082441] rounded-xl border border-[#192634] p-6">
                <h3 className="text-lg font-semibold text-[#FEFCEB] mb-4">Campaign preview</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#7F8585]">Title:</span>
                    <span className="text-[#FEFCEB]">{formData.title || 'Not set'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#7F8585]">Goal:</span>
                    <span className="text-[#FEFCEB]">£{formData.goal || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#7F8585]">Duration:</span>
                    <span className="text-[#FEFCEB]">
                      {formData.startDate && formData.endDate
                        ? `${new Date(formData.startDate).toLocaleDateString('en-GB')} - ${new Date(formData.endDate).toLocaleDateString('en-GB')}`
                        : 'Not set'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#7F8585]">Teams:</span>
                    <span className="text-[#FEFCEB]">{formData.selectedTeams.length} selected</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2.5 text-[#7F8585] hover:text-[#FEFCEB] transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleCreate}
                  className="bg-[#00E7FF] hover:bg-[#00d4e6] text-[#000F1F] px-8 py-2.5 rounded-full font-semibold transition-colors"
                >
                  Create Campaign
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateFundraising;
